import { useState } from 'react';
import { ExternalLink, File, FileText } from 'lucide-react';
import {
    DOWNLOAD_ATTR_FILE_ID,
    DOWNLOAD_ATTR_FILE_TITLE,
    DOWNLOAD_ATTR_PROFESSIONAL_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
    DOWNLOAD_ATTR_SURFACE,
    TRACK_DOWNLOAD_ATTR,
} from '@archipro-website/tracker';
import { DownloadSurface } from 'generated/graphql';

import { DownloadFileRow } from '~/ui-rocco/pages/detail-shared';
import {
    DownloadSaveButton,
    type GeneratedDocument,
} from '~/ui-rocco/pages/detail-shared/DownloadSaveButton';
import {
    ARCHIPRO_LOOK_BOOK_ID,
    ARCHIPRO_SPEC_SHEET_ID,
} from '@modules/product/tear-sheet/archipro-spec-sheet';
import {
    downloadCategoryTitle,
    downloadFileTypeLabel,
    type DownloadFileFields,
} from '../../utils/downloads';

/** Shown in the row's Size column when the backend reports no file size. */
export const UNKNOWN_SIZE = '—';

export interface DownloadRowFile extends DownloadFileFields {
    ID: number;
    Title: string;
    FileSize?: string | null;
    /** `Supplier` for an uploaded file, `ArchiPro` for one we generate on request. */
    Source?: string | null;
    FileCategory?: {
        Title?: string | null;
        SortOrder?: number | null;
        Group?: string | null;
    } | null;
}

/**
 * The gate the owning item's files are behind, shared by every row in a group so
 * one lead covers the group rather than one per file.
 */
export interface DownloadRowGate {
    /**
     * The surface a row reports when it is not a document that names its own —
     * the gate's default, which the rows have to repeat on their anchors
     * because an ungated download is a plain link the gate never sees.
     */
    surface: DownloadSurface;
    /** The reader has not yet paid for this professional's files. */
    isGated: boolean;
    /** A logged-out reader meets the sign-in wall before anything else. */
    isLoggedIn: boolean;
    /** Stop the reader at the gate, and hand the file over on the far side. */
    onGatedDownload: (
        link: string,
        takenFrom?: DownloadSurface,
        /** What this row knows about the file, for the gate's own logging. */
        file?: { title?: string; id?: number; professionalTitle?: string },
    ) => void;
    /** Raise the lead a file earns without handing the file over. */
    registerDownloadLead: (takenFrom?: DownloadSurface) => void;
}

interface DownloadRowProps {
    download: DownloadRowFile;
    professionalID: number;
    professionalTitle: string;
    /** Decided once for the whole tab, so rows stay aligned with each other. */
    showFileColumn: boolean;
    /** The page this file hangs off, and the one a generated document is made from. */
    itemID: number;
    gate: DownloadRowGate;
}

/** The file's destination — an external link wins over an internal one. */
export const downloadHref = (download: DownloadRowFile): string =>
    download.ExternalLink || download.InternalLink || '';

export const DownloadRow = ({
    download,
    professionalID,
    professionalTitle,
    showFileColumn,
    itemID,
    gate,
}: DownloadRowProps) => {
    const [hasDownloaded, setHasDownloaded] = useState(false);
    const href = downloadHref(download);

    // One rule for every file ArchiPro hands over, so a file listed here is
    // gated exactly as it is on the page it belongs to.
    const isGated = gate.isGated;

    // A gated file must not put its URL in the DOM, so the row becomes a button
    // and loses the delegated anchor tracking with it. The gate logs the click
    // for every surface that goes through it; this row only has to say what it
    // knows about the file. It used to log here as well, which double-counted
    // once the gate started reporting.
    const handleGatedDownload = () => {
        gate.onGatedDownload(href, takenFrom, {
            title: download.Title,
            id: fileID,
            professionalTitle,
        });
        setHasDownloaded(true);
    };

    // External links are read as links first: their "extension" is whatever
    // follows the last dot of the whole URL, so a trailing path segment would
    // otherwise be printed as the filetype.
    // A tear sheet or lookbook is built by us on request rather than uploaded by
    // the professional, so the row says so where it names who supplied the file.
    const isGenerated = download.Source === 'ArchiPro';
    // A generated row stands in for a document rendered on request, so it has
    // no file record to pin; it is saved by its page plus which document it is.
    const generatedDocument: GeneratedDocument | null =
        download.ID === ARCHIPRO_SPEC_SHEET_ID
            ? 'TearSheet'
            : download.ID === ARCHIPRO_LOOK_BOOK_ID
              ? 'Lookbook'
              : null;

    // The tab lists files from across the profile, so its own surface is the
    // default; a document ArchiPro renders names itself on the attempt, which
    // is what tells the professional which of their files earned the lead.
    // Both generated documents are made from the product they describe
    // (`archiproLookBookFrom` derives the book's route from the tear sheet's),
    // so the book is a ProductLookbook. It reported ProjectLookbook, which is
    // the *project* book the lookbook tiles hand out — a different document.
    const takenFrom: DownloadSurface | undefined =
        generatedDocument === 'TearSheet'
            ? DownloadSurface.ProductTearSheet
            : generatedDocument === 'Lookbook'
              ? DownloadSurface.ProductLookbook
              : undefined;
    // A generated document's id is a sentinel (-1, -2) standing in for a file
    // record that does not exist, so nothing can be joined back to it.
    const fileID = download.ID > 0 ? download.ID : undefined;
    const typeLabel = downloadFileTypeLabel(download);
    const isExternal = !!download.ExternalLink && !isGenerated;
    const format = isGenerated
        ? 'PDF'
        : isExternal
          ? 'Link'
          : typeLabel || 'File';
    const Icon = isExternal
        ? ExternalLink
        : typeLabel || isGenerated
          ? FileText
          : File;

    return (
        <DownloadFileRow
            title={download.Title}
            uploader={isGenerated ? 'ArchiPro' : professionalTitle}
            fileLabel={downloadCategoryTitle(download)}
            showFileColumn={showFileColumn}
            // Every row on this tab is uploaded by the professional whose page
            // it is, and the title's sub-line already says so. The column would
            // print the same word 1,300 times.
            showUploaderColumn={false}
            format={format}
            size={download.FileSize || UNKNOWN_SIZE}
            Icon={Icon}
            hasDownloaded={hasDownloaded}
            saveControl={
                <DownloadSaveButton
                    {...(generatedDocument
                        ? ({
                              kind: 'generated',
                              document: generatedDocument,
                          } as const)
                        : ({ kind: 'file', fileId: download.ID } as const))}
                    title={download.Title}
                    itemId={itemID}
                    tone="white"
                    // The M step: the icon, this padding and the hairline make
                    // the Download button's own height, so the pair sits level
                    // and Save is square at rest.
                    className="h-[2.75rem] rounded-[1rem] px-[0.5625rem]"
                    // Saving a Specs file reaches it as surely as downloading
                    // does, so it owes the same lead. Only once signed in:
                    // signing in is what the save itself asks for first.
                    onSaveIntent={
                        isGated && gate.isLoggedIn
                            ? () => gate.registerDownloadLead(takenFrom)
                            : undefined
                    }
                />
            }
            // A download with neither an external nor an internal link has
            // nowhere to go, so the row carries no control at all.
            control={
                !href
                    ? undefined
                    : isGated
                      ? { kind: 'button', onDownload: handleGatedDownload }
                      : {
                            kind: 'link',
                            href,
                            // Tracked the way every other download anchor on the
                            // site is: the delegated `mousedown` listener in
                            // `useLogLinkClicks` reads these and logs
                            // `DownloadsClick` once per file URL per page. That
                            // path is the only one carrying the mouse button and
                            // the signed-in member, and it resolves `fileUrl`
                            // from the anchor's `.href`, so the URL logged is
                            // absolute. Logging here too would double-count.
                            dataAttributes: {
                                [TRACK_DOWNLOAD_ATTR]: '1',
                                [DOWNLOAD_ATTR_SURFACE]:
                                    takenFrom ?? gate.surface,
                                [DOWNLOAD_ATTR_FILE_ID]: fileID,
                                [DOWNLOAD_ATTR_FILE_TITLE]: download.Title,
                                [DOWNLOAD_ATTR_PROFESSIONAL_ID]: professionalID,
                                [DOWNLOAD_ATTR_PROFESSIONAL_TITLE]:
                                    professionalTitle,
                            },
                            onActivate: () => setHasDownloaded(true),
                        }
            }
        />
    );
};
